"use client";

import { useCallback, useRef, useState } from "react";
import { FileVideo, Library, Upload, Video, X } from "lucide-react";

interface SelectedLibraryVideo {
  id: string;
  title: string;
}

interface LessonMediaUploadCardProps {
  file: File | null;
  onFile: (file: File | null) => void;
  onRecord: () => void;
  onSelectExisting?: () => void;
  selectedVideo?: SelectedLibraryVideo | null;
  onClearSelected?: () => void;
  /**
   * A video already attached to the lesson as a direct URL (uploaded or
   * recorded). Rendered as an attached-state card so the user can see and
   * remove it — without this the card shows an empty dropzone even though
   * the lesson has a video.
   */
  attachedVideoDuration?: number | null;
  hasAttachedVideo?: boolean;
  onClearAttached?: () => void;
}

function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return `${mins}:${String(secs).padStart(2, "0")}`;
}

export function LessonMediaUploadCard({
  file,
  onFile,
  onRecord,
  onSelectExisting,
  selectedVideo,
  onClearSelected,
  attachedVideoDuration,
  hasAttachedVideo,
  onClearAttached,
}: LessonMediaUploadCardProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      const dropped = e.dataTransfer.files[0];
      if (dropped) onFile(dropped);
    },
    [onFile],
  );

  const handleFileInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selected = e.target.files?.[0];
      if (selected) onFile(selected);
      e.target.value = "";
    },
    [onFile],
  );

  const handleBrowseClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // A video from the library is selected
  if (selectedVideo) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Library size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {selectedVideo.title}
          </p>
          <p className="text-xs text-muted-foreground">From video library</p>
        </div>
        <button
          type="button"
          onClick={onClearSelected}
          className="flex-shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Remove video"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // The lesson already has an uploaded/recorded video attached
  if (hasAttachedVideo) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <Video size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            Video attached
          </p>
          <p className="text-xs text-muted-foreground">
            {attachedVideoDuration
              ? formatDuration(attachedVideoDuration)
              : "Uploaded video"}
          </p>
        </div>
        <button
          type="button"
          onClick={onClearAttached}
          className="flex-shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Remove video"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  // A local file has been selected
  if (file) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-border bg-card p-4">
        <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/10">
          <FileVideo size={20} className="text-primary" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-foreground">
            {file.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </p>
        </div>
        <button
          type="button"
          onClick={() => onFile(null)}
          className="flex-shrink-0 rounded p-1 text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
          aria-label="Remove file"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      className={`flex flex-col items-center justify-center rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
        isDragging
          ? "border-primary bg-primary/5"
          : "border-border hover:border-primary/50 hover:bg-accent/30"
      }`}
    >
      <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
        <Upload size={24} className="text-primary" />
      </div>
      <p className="mb-1 text-sm font-semibold text-foreground">
        Upload Your Media
      </p>
      <p className="mb-4 text-xs text-muted-foreground">
        Video, audio, or any media file
      </p>

      <div className="flex flex-wrap items-center justify-center gap-2">
        <button
          type="button"
          onClick={handleBrowseClick}
          className="rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          Browse files
        </button>

        <button
          type="button"
          onClick={onRecord}
          className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
        >
          <Video size={14} />
          Record
        </button>

        {onSelectExisting && (
          <button
            type="button"
            onClick={onSelectExisting}
            className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            <Library size={14} />
            Use existing
          </button>
        )}
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="video/*,audio/*"
        onChange={handleFileInputChange}
        className="hidden"
      />
    </div>
  );
}
